import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { CoverageBl, CoverageState } from './use-coverage-state';

// Fixed en-US grouping (comma thousands, dot decimal), locale-independent so it
// reads the same way the number input below parses.
function formatCents(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  return (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function RowMeta({ row }: Readonly<{ row: CoverageBl }>) {
  return (
    <div className="min-w-0 flex-1">
      <span className="text-sm font-medium">{row.name ?? `BL ${row.blOrderId}`}</span>
      {row.quantityMt !== null && (
        <span className="text-muted-foreground ml-2 text-xs">· {row.quantityMt} MT</span>
      )}
      {row.appliedFloor > 0 && (
        <span className="text-muted-foreground ml-2 text-xs">
          · {formatCents(row.appliedFloor * 100)} consumed
        </span>
      )}
    </div>
  );
}

interface CoverageRowProps {
  readonly row: CoverageBl;
  readonly currency: string;
  readonly state: CoverageState;
  readonly readOnly: boolean;
}

function CoverageRow({ row, currency, state, readOnly }: CoverageRowProps) {
  const rowState = state.map[row.blOrderId];
  const covered = rowState?.covered ?? false;
  return (
    <li
      className={cn(
        'border-border flex flex-wrap items-center gap-x-3 gap-y-2 border-b p-3 last:border-b-0',
        !covered && 'opacity-60',
      )}
    >
      <Checkbox
        checked={covered}
        disabled={readOnly || row.appliedFloor > 0}
        aria-label={`Cover ${row.name ?? row.blOrderId}`}
        onCheckedChange={(next) => {
          state.setCovered(row.blOrderId, next === true);
        }}
      />
      <RowMeta row={row} />
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="decimal"
          className="w-32 text-right tabular-nums"
          disabled={!covered || readOnly}
          value={rowState?.amount ?? ''}
          aria-label={`Share for ${row.name ?? row.blOrderId}`}
          onChange={(e) => {
            state.setAmount(row.blOrderId, e.target.value);
          }}
        />
        <span className="text-muted-foreground text-xs">{currency}</span>
      </div>
    </li>
  );
}

export interface CoveragePickerProps {
  readonly rows: readonly CoverageBl[];
  readonly currency: string;
  readonly state: CoverageState;
}

// The per-BL coverage editor: check the BLs an advance covers and set each
// one's money share. The shares must add up to the advance total before the
// caller's Save enables — the pill turns green only on an exact match. A
// single-BL order carries the whole total on its one row, read-only.
export function CoveragePicker({ rows, currency, state }: CoveragePickerProps) {
  const single = rows.length === 1;
  return (
    <div className="space-y-2">
      <ul className="border-border overflow-hidden rounded-md border">
        {rows.map((row) => (
          <CoverageRow
            key={row.blOrderId}
            row={row}
            currency={currency}
            state={state}
            readOnly={single}
          />
        ))}
      </ul>
      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="text-muted-foreground">Allocated</span>
        <span
          className={cn(
            'rounded px-2 py-0.5 font-medium tabular-nums',
            state.isValid
              ? 'bg-success text-success-foreground'
              : 'bg-destructive text-destructive-foreground',
          )}
        >
          {formatCents(state.allocatedCents)} / {formatCents(state.totalCents)} {currency}
          {state.isValid ? ' ✓' : ''}
        </span>
      </div>
      {state.belowFloor && (
        <p className="text-destructive text-xs">
          A covered BL's share is below what it has already consumed.
        </p>
      )}
    </div>
  );
}
